<template>
  <div>
    <vx-card class="mb-5" title="The Four Principles of Medical Ethics">
      <p class="mb-5">
        Source:
        <a
          href="https://web.stanford.edu/class/siw198q/websites/reprotech/New Ways of Making Babies/EthicVoc.htm"
          target="_blank"
          >Stanford University</a
        >
      </p>
      <p class="mb-5">
        When working in medicine or medical-related services, there are some basic things that
        should be considered before administering any kind of treatment. These are referred to as
        the Basic Principles of Medical Ethics.
      </p>
      <p class="mb-5">
        For a medical practice to be considered "ethical", it must respect all four of these
        principles: autonomy, justice, beneficence, and non-maleficence. Let’s take a look at what
        those terms mean.
      </p>

      <h5 class="mb-5">Autonomy</h5>
      <p class="mb-5">
        Autonomy is usually described as the right of competent adults to make informed decisions
        about their own medical care. This means that patients should understand the risks and
        benefits of the procedure, and not have been coerced or coaxed into making a particular
        decision.
      </p>
      <p class="mb-5">
        Autonomy is a very complex subject which involves the law, ethics and philosophy. If you’re
        interested in learning more about it, check out this resource by the
        <a
          href="https://www.bma.org.uk/advice-and-support/ethics/medical-students/ethics-toolkit-for-medical-students/autonomy-or-self-determination#:~:text=What%20do%20we%20mean%20by,investigation%20or%20treatment%20takes%20place."
          target="_blank"
          >British Medical Association</a
        >.
      </p>

      <h5 class="mb-5">Justice</h5>
      <p class="mb-5">
        Justice in the context of medicine is the principle that when weighing up if something is
        ethical or not, we have to think about whether it’s compatible with the law, the patient’s
        rights, and if it’s fair and balanced.
      </p>
      <p class="mb-5">
        When thinking about the principle of justice relating to a procedure, you would ask yourself
        these questions:
      </p>

      <ul class="default-list">
        <li>Is this action legal?</li>
        <li>Does this action unfairly contradict someone’s human rights?</li>
        <li>Does this action prioritise one group over another?</li>
        <li>
          If it does prioritise one group over another, can it be justified in terms of overall net
          benefit to society or does it agree with moral conventions?
        </li>
      </ul>

      <p class="mb-5">
        (source:
        <a
          href="https://www.themedicportal.com/application-guide/medical-school-interview/medical-ethics/justice/#:~:text=What%20is%20Justice%3F,if%20it's%20fair%20and%20balanced."
          target="_blank"
          >themedicportal.com</a
        >)
      </p>

      <h5 class="mb-5">Beneficence</h5>
      <p class="mb-5">
        Put simply, are you providing the treatment with the intention of doing good for the
        patient, and are you up-to-date and trained when it comes to administering your medical
        practice?
      </p>
      <p class="mb-5">Ask yourself:</p>

      <ol class="numbered-list">
        <li>Will this option resolve this patient’s medical problem?</li>
        <li>Is it proportionate to the scale of the medical problem?</li>
        <li>Is this option compatible with this patient’s individual circumstances?</li>
        <li>
          Is this option and its outcomes in-line with the patient’s expectations of treatment?
        </li>
      </ol>

      <p class="mb-5">
        (source:
        <a
          href="https://www.themedicportal.com/application-guide/medical-school-interview/medical-ethics/medical-ethics-beneficence/"
          target="_blank"
          >themedicportal.com</a
        >)
      </p>
      <p class="mb-5">
        You can also see here that there is a large emphasis on what the patient sees as beneficial,
        and the treatments’ suitability to someone’s circumstances.
      </p>

      <h5 class="mb-5">Non-maleficence</h5>
      <p class="mb-5">From The Medic Portal:</p>
      <p class="mb-5">
        <i
          >“Non-maleficence states that a medical practitioner has a duty to do no harm or allow
          harm to be caused to a patient through neglect. Any consideration of beneficence is
          likely, therefore, to involve an examination of non-maleficence.</i
        >
      </p>
      <p class="mb-5"><i>“Non-maleficence differs from beneficence in two major ways.</i></p>
      <p class="mb-5">
        <i
          >First of all, it acts as a threshold for treatment. If a treatment causes more harm than
          good, then it should not be considered. This is in contrast to beneficence, where we
          consider all valid treatment options and then rank them in order of preference.</i
        >
      </p>
      <p class="mb-5">
        <i
          >Second, we tend to use beneficence in response to a specific situation – such as
          determining the best treatment for a patient. In contrast, non-maleficence is a constant
          in clinical practice. For example, if you see a patient collapse in a corridor you have a
          duty to provide (or seek) medical attention to prevent injury.”</i
        >
      </p>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">The Declaration of Geneva</h4>
      <p class="mb-5">
        You may have heard of the Hippocratic Oath, or even the classic quote “First, do no harm”.
        The Hippocratic Oath was one of the first documents created that asked medical professionals
        to uphold a certain set of professional ethical standards.
      </p>
      <p class="mb-5">
        These days, there are updated documents, including the Declaration of Geneva, which new
        medical practitioners are asked to avow before they begin their work as a licensed doctor.
        They were created by the World Medical Association after the Second World War as a
        modernised version of the Hippocratic Oath, and have been updated from time to time to
        reflect changing societal standards.
      </p>
      <p class="mb-5">
        The Declaration of Geneva (2017), as currently published by the World Medical Association
        reads:
      </p>
      <p class="mb-5">AS A MEMBER OF THE MEDICAL PROFESSION:</p>
      <p class="mb-5">I SOLEMNLY PLEDGE to dedicate my life to the service of humanity;</p>
      <p class="mb-5">THE HEALTH AND WELL-BEING OF MY PATIENT will be my first consideration;</p>
      <p class="mb-5">I WILL RESPECT the autonomy and dignity of my patient;</p>
      <p class="mb-5">I WILL MAINTAIN the utmost respect for human life;</p>
      <p class="mb-5">
        I WILL NOT PERMIT considerations of age, disease or disability, creed, ethnic origin,
        gender, nationality, political affiliation, race, sexual orientation, social standing or any
        other factor to intervene between my duty and my patient;
      </p>
      <p class="mb-5">
        I WILL RESPECT the secrets that are confided in me, even after the patient has died;
      </p>
      <p class="mb-5">
        I WILL PRACTICE my profession with conscience and dignity and in accordance with good
        medical practice;
      </p>
      <p class="mb-5">I WILL FOSTER the honour and noble traditions of the medical profession;</p>
      <p class="mb-5">
        I WILL GIVE to my teachers, colleagues, and students the respect and gratitude that is their
        due;
      </p>
      <p class="mb-5">
        I WILL SHARE my medical knowledge for the benefit of the patient and the advancement of
        healthcare;
      </p>
      <p class="mb-5">
        I WILL ATTEND TO my own health, well-being, and abilities in order to provide care of the
        highest standard;
      </p>
      <p class="mb-5">
        I WILL NOT USE my medical knowledge to violate human rights and civil liberties, even under
        threat;
      </p>
      <p class="mb-5">I MAKE THESE PROMISES solemnly, freely and upon my honour.</p>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">Multidisciplinary care</h4>

      <p class="mb-5">
        Multidisciplinary care is incredibly important. It means working alongside and involving
        other health professionals (nurses, physios, mental health providers, dietitians etc.) to
        provide the patient with the best possible care.
      </p>
      <p class="mb-5">
        Some of the values that underlie an effective multidisciplinary team include:
      </p>

      <ul class="default-list">
        <li>Effective communication and coordination</li>
        <li>Respect and trust</li>
        <li>Solid implementation strategies</li>
        <li>Transparency</li>
      </ul>

      <p class="mb-5">
        A useful resource to take a look at is a checklist for Principles of Multidisciplinary care,
        that was developed by Cancer Australia, which asks the following questions:
      </p>

      <h4 class="mb-5">Team</h4>
      <ul class="default-list">
        <li>
          Is there an established multidisciplinary team comprising all core disciplines integral to
          good patient care?
        </li>
        <li>Is the patient’s GP included as a team member and kept informed of team activity?</li>
        <li>
          Have referral links been established with non-core team specialist services (e.g. genetic
          counselling, physiotherapy)?
        </li>
      </ul>

      <h4 class="mb-5">Communication</h4>
      <ul class="default-list">
        <li>Are regular multidisciplinary team meetings held to discuss treatment planning?</li>
        <li>
          Has a communications framework been established to facilitate case discussion by all
          members?
        </li>
        <li>
          Do all core disciplines attend the multidisciplinary team meetings – diagnostic, treatment
          and supportive care?
        </li>
        <li>
          Is there a protocol to decide which patients should be discussed at multidisciplinary team
          meetings?
        </li>
      </ul>

      <h4 class="mb-5">Full therapeutic range</h4>
      <ul class="default-list">
        <li>Do all patients have access to relevant services?</li>
      </ul>

      <h4 class="mb-5">Standards of care</h4>
      <ul class="default-list">
        <li>
          Are protocols and clinical pathways available? If yes, are processes in place to ensure
          that local practice is in line with guidelines?
        </li>
        <li>Are patient preferences discussed in multidisciplinary team meetings?</li>
        <li>
          Are all relevant test results, reports and films available during multidisciplinary
          meetings?
        </li>
        <li>
          Is the number of cases seen at the site small? If yes, are collaborative links with larger
          units/centres in place?
        </li>
        <li>Are professional development activities held regularly?</li>
      </ul>

      <h4 class="mb-5">Involvement of the patient</h4>
      <ul class="default-list">
        <li>
          Are patients routinely offered information about all aspects of their treatment choices?
        </li>
        <li>
          Are patients routinely given information about and access to supportive care services?
        </li>
        <li>Are patients informed that their care is discussed in a multidisciplinary forum?</li>
      </ul>

      <h4 class="mb-5">Principle of Care</h4>
      <ul class="default-list">
        <li>Is patient consent obtained according to local protocol?</li>
        <li>Are patients informed who the ‘team leader’ is?</li>
      </ul>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';

export default {
  data() {
    return {
      videoURL: false,
      showVideoPopupFile: false,
      showVideoPopupYt: false,
      ytVideoUrl: false,
      image1: require('@/assets/images/experiences/teacher/bloom_taxonomy.jpg'),
    };
  },
  provide: function () {
    return {
      closePopup: this.closePopup,
    };
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
